import { useRef, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { useLogo } from "@/lib/useLogo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Loader2, ImageIcon } from "lucide-react";
import { UnionInfoCard } from "@/components/UnionInfoCard";
import { EmailScheduleCard } from "@/components/EmailScheduleCard";
import { ReminderDaysCard } from "@/components/ReminderDaysCard";
import { useAuth } from "@/context/AuthContext";

export default function SettingsPage() {
  const { isAdmin } = useAuth();
  if (!isAdmin) {
    return (
      <AppLayout title="Ayarlar" subtitle="Dilekçe hatırlatma ayarı">
        <ReminderDaysCard />
      </AppLayout>
    );
  }
  return <AdminSettings />;
}

function AdminSettings() {
  const { url, loaded, reload } = useLogo();
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo yüklendi");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo yüklenemedi");
    } finally {
      setUploading(false);
    }
  };

  return (
    <AppLayout title="Ayarlar" subtitle="Birlik bilgileri ve rapor görünümü">
      <UnionInfoCard />
      <Card className="p-6 border border-slate-200 shadow-sm max-w-2xl" data-testid="logo-settings-card">
        <h3 className="font-display font-semibold text-slate-900">Birlik Logosu</h3>
        <p className="text-sm text-slate-500 mt-1">
          Logo, köy özet formunun, tüm PDF raporların ve Excel raporlarının başlığında görünür. PNG, JPG veya WEBP, en fazla 5MB.
          Şeffaf arka planlı PNG en iyi sonucu verir.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-6">
          <div className="h-28 w-28 rounded-md border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden"
               data-testid="logo-preview">
            {url ? <img src={url} alt="Birlik logosu" className="max-h-full max-w-full object-contain" data-testid="logo-preview-image" />
                 : <ImageIcon className="h-8 w-8 text-slate-300" />}
          </div>
          <div>
            <div className="text-sm text-slate-600 mb-3" data-testid="logo-status">
              {!loaded ? "Yükleniyor..." : url ? "Logo yüklü." : "Henüz logo yüklenmedi."}
            </div>
            <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                   onChange={onFile} data-testid="logo-file-input" />
            <Button onClick={() => inputRef.current?.click()} disabled={uploading}
                    className="bg-emerald-700 hover:bg-emerald-800" data-testid="logo-upload-button">
              {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
              {url ? "Logoyu Değiştir" : "Logo Yükle"}
            </Button>
          </div>
        </div>
      </Card>
      <ReminderDaysCard />
      <EmailScheduleCard endpoint="/settings/backup-email" prefix="backup-email" title="Otomatik Haftalık Yedek"
        emailLabel="Yedek E-posta Adresi" sentToast="Yedek e-postayla gönderildi"
        description="Her Cuma saat 17:00'de (Türkiye saati) tam Excel yedeği aşağıdaki adrese e-posta eki olarak gönderilir. Adresi boş bırakırsanız gönderim yapılmaz." />
      <EmailScheduleCard endpoint="/settings/petition-reminder" prefix="petition-reminder" title="Bekleyen Dilekçe Hatırlatması"
        emailLabel="Hatırlatma E-posta Adresi" sentToast="Hatırlatma e-postası gönderildi"
        description="Her Pazartesi saat 08:00'de (Türkiye saati) yukarıdaki gün sınırından uzun süredir beklemede veya işlemde olan dilekçelerin listesi aşağıdaki adrese gönderilir. Böyle dilekçe yoksa e-posta gönderilmez."
        extra={(cfg) => (
          <div className="text-sm" data-testid="petition-reminder-overdue-count">
            Şu an {cfg.days ?? 30} günden uzun bekleyen dilekçe: <span className="font-semibold text-slate-900">{cfg.overdue_count ?? 0}</span>
          </div>
        )} />
    </AppLayout>
  );
}
